import { ref, get, set, update, remove } from 'firebase/database';
import { database } from '../config/firebase';
import {
  ServiceRequest,
  ServiceRequestMessage,
  ServiceRequestType,
  ServiceRequestStatus,
  ServiceRequestPriority,
  PricingPlan,
  ActiveService,
  ShopierTransaction,
} from '../types';
import { dbService } from './firebase';

const STORAGE_PREFIX = 'adiosoft_marketplace_';

export class ServiceMarketplaceService {
  private getLocal<T>(key: string, defaultValue: T): T {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      return raw ? JSON.parse(raw) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private setLocal<T>(key: string, value: T): void {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
    } catch (err) {
      console.warn('Marketplace local storage write warning:', err);
    }
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;
  }

  // ==============================================================
  // 1. SERVICE REQUESTS (Talepler & Proje Yönetimi)
  // ==============================================================

  public async getRequests(userId?: string, isSuperAdmin = false): Promise<ServiceRequest[]> {
    if (database) {
      try {
        const snapshot = await get(ref(database, 'serviceRequests'));
        if (snapshot.exists()) {
          const list: ServiceRequest[] = Object.values(snapshot.val());
          return isSuperAdmin ? list : (userId ? list.filter((r) => r.userId === userId) : list);
        }
      } catch (err) {
        console.warn('RTDB serviceRequests get warning:', err);
      }
    }

    const local = this.getLocal<ServiceRequest[]>('requests', []);
    return isSuperAdmin ? local : (userId ? local.filter((r) => r.userId === userId) : local);
  }

  public async getRequestById(id: string): Promise<ServiceRequest | null> {
    const list = await this.getRequests(undefined, true);
    return list.find((r) => r.id === id) || null;
  }

  public async createRequest(
    data: Omit<ServiceRequest, 'id' | 'createdAt' | 'updatedAt' | 'messages' | 'status'> & {
      status?: ServiceRequestStatus;
      messages?: ServiceRequestMessage[];
    }
  ): Promise<ServiceRequest> {
    const now = Date.now();
    const id = this.generateId('req');
    const newReq: ServiceRequest = {
      ...data,
      id,
      status: data.status || 'NEW',
      priority: data.priority || 'MEDIUM',
      messages: data.messages || [],
      attachments: data.attachments || [],
      createdAt: now,
      updatedAt: now,
    };

    if (database) {
      try {
        await set(ref(database, `serviceRequests/${id}`), newReq);
      } catch (err) {
        console.warn('RTDB serviceRequest write error, using fallback:', err);
      }
    }

    const current = this.getLocal<ServiceRequest[]>('requests', []);
    this.setLocal('requests', [newReq, ...current]);
    return newReq;
  }

  public async updateRequest(id: string, updates: Partial<ServiceRequest>): Promise<ServiceRequest> {
    const all = await this.getRequests(undefined, true);
    const existing = all.find((r) => r.id === id);
    if (!existing) throw new Error('Hizmet talebi bulunamadı');

    const updated: ServiceRequest = {
      ...existing,
      ...updates,
      updatedAt: Date.now(),
    };

    if (database) {
      try {
        await update(ref(database, `serviceRequests/${id}`), updated);
      } catch (err) {
        console.warn('RTDB serviceRequest update error:', err);
      }
    }

    const local = this.getLocal<ServiceRequest[]>('requests', []);
    const next = local.map((r) => (r.id === id ? updated : r));
    this.setLocal('requests', next);
    return updated;
  }

  public async addMessage(
    requestId: string,
    sender: 'CLIENT' | 'ADIOSOFT',
    senderName: string,
    message: string
  ): Promise<ServiceRequest> {
    const req = await this.getRequestById(requestId);
    if (!req) throw new Error('Talep bulunamadı');

    const newMsg: ServiceRequestMessage = {
      id: this.generateId('msg'),
      sender,
      senderName,
      message,
      timestamp: Date.now(),
    };

    const updatedMessages = [...(req.messages || []), newMsg];
    return this.updateRequest(requestId, { messages: updatedMessages });
  }

  public async deleteRequest(id: string): Promise<void> {
    if (database) {
      try {
        await remove(ref(database, `serviceRequests/${id}`));
      } catch (err) {
        console.warn('RTDB deleteRequest error:', err);
      }
    }
    const local = this.getLocal<ServiceRequest[]>('requests', []);
    this.setLocal('requests', local.filter((r) => r.id !== id));
  }

  // ==============================================================
  // 2. PRICING PLANS (Admin-Managed Dynamic Pricing)
  // ==============================================================

  public async getPricingPlans(): Promise<PricingPlan[]> {
    const defaultPlans: PricingPlan[] = [
      {
        id: 'price_corp_annual',
        name: 'Kurumsal Web Sitesi Paketi',
        serviceType: 'WEBSITE_CORP',
        period: 'ANNUAL',
        priceTRY: 15000,
        websiteLimit: 1,
        description: 'Yıllık 15.000 TL paket. 1 Web Sitesi Hakkı, AI Web Sitesi Üretici, Görsel Editör, sınırsız sayfalar, hosting ve SSL dahildir.',
        features: [
          'Website Hakkı: 1 Web Sitesi (Maksimum 1 Site/Domain)',
          'AI Web Sitesi Generator & Görsel Editör',
          'Sınırsız Sayfa & Menü Yönetimi',
          'Kurumsal Blog & SEO Altyapısı',
          'Özel Alan Adı Bağlama & Ücretsiz SSL',
          'Yıllık Kesintisiz Bulut Hosting',
        ],
        isPopular: true,
        updatedAt: Date.now(),
      },
      {
        id: 'price_yacht_annual',
        name: 'Yat Kiralama Web Sitesi Paketi',
        serviceType: 'WEBSITE_YACHT',
        period: 'ANNUAL',
        priceTRY: 36000,
        websiteLimit: 1,
        description: 'Yıllık 36.000 TL paket. 1 Web Sitesi Hakkı, Yat filosu, müsaitlik takvimi, online rezervasyon talep motoru ve VIP tema desteği.',
        features: [
          'Website Hakkı: 1 Web Sitesi (Maksimum 1 Site/Domain)',
          'Gelişmiş Yat Filosu & Rota Yönetimi',
          'Müsaitlik ve Doluluk Takvimi',
          'Online Rezervasyon Talep Motoru',
          'Teknik Özellikler ve VIP Galeri',
          'VIP Bulut Altyapısı ve 7/24 Destek',
        ],
        isPopular: true,
        updatedAt: Date.now(),
      },
      {
        id: 'price_external_license',
        name: 'Harici Hosting Lisans Entegrasyonu',
        serviceType: 'CUSTOM_SOFTWARE',
        period: 'PROJECT_BASED',
        priceTRY: 5000,
        description: 'GoDaddy, cPanel veya özel sunucunuza güvenli PHP lisans scripti kurulumu ve Realtime Database bağlantısı.',
        features: [
          'Bağımsız PHP Doğrulama Scripti Kurulumu',
          'GoDaddy / cPanel / Nginx / Apache Uyumluluğu',
          'Otomatik 2 Saatlik Dosya Önbellekleme',
          'HTTP 503 Servis Dışı Güvenlik Kalkanı',
          'Super Admin Panelinden Anlık Kontrol',
        ],
        isPopular: true,
        updatedAt: Date.now(),
      },
      {
        id: 'price_mobile_app',
        name: 'Mobil Uygulama (iOS & Android)',
        serviceType: 'MOBILE_APP',
        period: 'PROJECT_BASED',
        priceTRY: 65000,
        description: 'App Store ve Google Play için modern native / cross-platform mobil uygulama çözümleri.',
        features: [
          'iOS ve Android Mağaza Uyumluluğu',
          'Özel Figma UI/UX Tasarımı',
          'Push Bildirim & Bildirim Paneli',
          'Güvenli API & Veritabanı Entegrasyonu',
          'Mağaza Yayınlama ve Onay Desteği',
        ],
        updatedAt: Date.now(),
      },
      {
        id: 'price_custom_software',
        name: 'Özel Yazılım & CRM Çözümleri',
        serviceType: 'CUSTOM_SOFTWARE',
        period: 'PROJECT_BASED',
        priceTRY: 85000,
        description: 'İşletmenizin operasyonel süreçlerine özel web tabanlı yazılımlar ve sistem entegrasyonları.',
        features: [
          'Özel İş Akışı ve Rol Bazlı Yetkilendirme',
          'ERP / Muhasebe / CRM Entegrasyonu',
          'Yönetici Dashboard ve Canlı Raporlar',
          'Ölçeklenebilir Bulut Altyapısı',
          'Kaynak Kod ve Teknik Dokümantasyon',
        ],
        updatedAt: Date.now(),
      },
      {
        id: 'price_google_business',
        name: 'Google İşletme Yönetimi & Yerel SEO',
        serviceType: 'GOOGLE_BUSINESS',
        period: 'PROJECT_BASED',
        priceTRY: 5000,
        description: 'Google Haritalar ve İşletme Profilinizin doğrulanması, yerel aramalarda ilk sıralara yükselmesi.',
        features: [
          'Harita Konum Doğrulama & İğne Ayarı',
          'Kategori, Hizmet ve Görsel Optimizasyonu',
          'Müşteri Yorum Yanıtlama Stratejisi',
          'Haftalık Google Gönderileri ve Güncellemeler',
          'Yerel Arama Sıralama Raporu',
        ],
        updatedAt: Date.now(),
      },
    ];

    const local = this.getLocal<PricingPlan[]>('pricing_plans', defaultPlans);
    if (database) {
      try {
        const snapshot = await get(ref(database, 'pricingPlans'));
        if (snapshot.exists()) {
          return Object.values(snapshot.val());
        }
      } catch (err) {
        console.warn('RTDB pricingPlans fetch error:', err);
      }
    }
    return local;
  }

  public async updatePricingPlan(id: string, updates: Partial<PricingPlan>): Promise<PricingPlan> {
    const plans = await this.getPricingPlans();
    const existing = plans.find((p) => p.id === id);
    if (!existing) throw new Error('Fiyatlandırma planı bulunamadı');

    const updated: PricingPlan = {
      ...existing,
      ...updates,
      updatedAt: Date.now(),
    };

    if (database) {
      try {
        await update(ref(database, `pricingPlans/${id}`), updated);
      } catch (err) {
        console.warn('RTDB updatePricingPlan error:', err);
      }
    }

    const next = plans.map((p) => (p.id === id ? updated : p));
    this.setLocal('pricing_plans', next);
    return updated;
  }

  // ==============================================================


  // ==============================================================
  // 4. ACTIVE SERVICES & SHOPIER PAYMENT VERIFICATION FLOW
  // ==============================================================

  public async getActiveServices(userId: string): Promise<ActiveService[]> {
    if (database) {
      try {
        const snapshot = await get(ref(database, `activeServices/${userId}`));
        if (snapshot.exists()) {
          return Object.values(snapshot.val());
        }
      } catch (err) {
        console.warn('RTDB activeServices fetch error:', err);
      }
    }

    return this.getLocal<ActiveService[]>(`active_services_${userId}`, []);
  }

  public async processServicePayment(payload: {
    userId: string;
    siteId?: string;
    serviceType: string;
    planId?: string;
    serviceName: string;
    amountTRY: number;
    period: 'ANNUAL' | 'MONTHLY' | 'ONE_TIME';
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    cardHolderName?: string;
    cardNumber?: string;
  }): Promise<{
    success: boolean;
    activeService: ActiveService;
    transaction: ShopierTransaction;
    message: string;
  }> {
    // 1. Simulate secure Shopier Gateway communication & backend verification
    await new Promise((resolve) => setTimeout(resolve, 1400));

    // Backend verification check: Verify signature and amount integrity
    if (!payload.amountTRY || payload.amountTRY <= 0) {
      throw new Error('Geçersiz ödeme tutarı: İşlem durduruldu.');
    }

    const now = Date.now();
    const orderId = `SHP_${Date.now().toString(36).toUpperCase()}_${Math.floor(1000 + Math.random() * 9000)}`;
    const txId = `SHP-${Math.floor(10000 + Math.random() * 90000)}`;

    let durationMs = 365 * 86400000; // default annual
    if (payload.period === 'MONTHLY') {
      durationMs = 30 * 86400000;
    } else if (payload.period === 'ONE_TIME') {
      durationMs = 365 * 10 * 86400000; // 10 years permanent
    }

    const expireDate = now + durationMs;

    // 2. Create Active Service Record
    const serviceId = `srv_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 6)}`;
    const newActiveService: ActiveService = {
      id: serviceId,
      userId: payload.userId,
      siteId: payload.siteId,
      serviceType: payload.serviceType,
      serviceName: payload.serviceName,
      status: 'ACTIVE',
      planId: payload.planId,
      priceTRY: payload.amountTRY,
      period: payload.period,
      startDate: now,
      expireDate,
      lastPaymentId: txId,
      shopierOrderId: orderId,
      details: {
        customerName: payload.customerName,
        customerEmail: payload.customerEmail,
        customerPhone: payload.customerPhone,
      },
      createdAt: now,
    };

    // 3. Persist Active Service to Database
    if (database) {
      try {
        await set(ref(database, `activeServices/${payload.userId}/${serviceId}`), newActiveService);
      } catch (err) {
        console.warn('RTDB activeServices save error:', err);
      }
    }

    const existingServices = await this.getActiveServices(payload.userId);
    const updatedServices = [newActiveService, ...existingServices.filter((s) => s.id !== serviceId)];
    this.setLocal(`active_services_${payload.userId}`, updatedServices);

    // 4. Save Payment Transaction to Database for Accounting & Auditing
    const transaction: ShopierTransaction = {
      id: txId,
      siteId: payload.siteId || 'TENANT_GLOBAL',
      customerName: payload.customerName || payload.cardHolderName || 'Müşteri',
      customerEmail: payload.customerEmail,
      planName: payload.serviceName,
      amount: payload.amountTRY,
      currency: 'TRY',
      createdAt: now,
      status: 'SUCCESS',
    };

    await dbService.savePaymentTransaction(transaction);

    // 5. If it is a website package, also update the site subscription in the database
    if (payload.siteId && (payload.serviceType === 'WEBSITE_CORP' || payload.serviceType === 'WEBSITE_YACHT')) {
      try {
        const site = await dbService.getSiteById(payload.siteId);
        if (site) {
          site.subscription = {
            siteId: site.id,
            planId: payload.planId || 'PLAN_CORP_ANNUAL',
            planName: payload.serviceName,
            priceTRY: payload.amountTRY,
            amount: payload.amountTRY,
            currency: 'TRY',
            status: 'ACTIVE',
            startDate: now,
            expireDate,
            lastPaymentId: txId,
            paymentId: txId,
            shopierOrderId: orderId,
            autoRenew: true,
          };
          if (site.status === 'SUSPENDED') {
            site.status = (site.publishedVersion || 0) > 0 ? 'PUBLISHED' : 'DRAFT';
          }
          await dbService.updateSite(site);
        }
      } catch (err) {
        console.warn('Site subscription sync error:', err);
      }
    }

    return {
      success: true,
      activeService: newActiveService,
      transaction,
      message: `${payload.serviceName} ödemesi Shopier üzerinden başarıyla doğrulandı ve hizmetiniz aktif edildi!`,
    };
  }
}

export const serviceMarketplaceService = new ServiceMarketplaceService();
export default serviceMarketplaceService;
